import React, { useState } from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { AccountCircleOutlined, Delete as DeleteIcon, EditOutlined as EditOutlinedIcon } from '@mui/icons-material';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { IAudition, IRole } from '../../types/global/helper';
import CustomButton from '../Button/CustomButton';
import { useDispatch } from 'react-redux';
import { setRole, deleteRole } from '../../redux/audition/actions';
import { toggleAuthModal } from '../../redux/authentication/action';
import { DialogueSheet } from '../DialogueSheet/DialogueSheet';
import { useAuth } from '../../hook/useAuth';
import { RichTextViewer } from '../Common/RichTextViewer';
import moment from 'moment';
import { AUDITION_FIELD_ACCENT } from './auditionFieldTheme';
import {
    BRAND_LUXE_SECONDARY,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER_LOW,
} from '../../pages/BrandDashboard/brandDashboardTokens';
import { alpha } from '@mui/material/styles';

export interface IRoleRenderer {
    audition: IAudition;
    onApplyClick?: (role: IRole) => void;
    isManageMode?: boolean;
    onManageClick?: (role: IRole) => void;
    isBrandMode?: boolean;
    manageControls?: React.ReactNode;
}

function roleTypeLabel(role: IRole): string {
    const rt = role.role_type;
    if (rt == null || rt === '') return '';
    if (typeof rt === 'string') return rt;
    if (typeof rt === 'object' && rt !== null && 'label' in rt) {
        return String((rt as { label: string }).label);
    }
    return String(rt);
}

const RoleRenderer: React.FC<IRoleRenderer> = ({
    audition,
    onApplyClick,
    isManageMode,
    onManageClick,
    isBrandMode = false,
    manageControls,
}) => {
    const { roles: auditionRoles } = audition;
    const roles = auditionRoles.filter(role => role !== null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState<IRole | null>(null);
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const { isSignedIn } = useAuth();

    const castingClosed = !!audition.casting_expiration && moment(audition.casting_expiration).isBefore(moment());

    const handleDeleteClick = (role: IRole) => {
        setRoleToDelete(role);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = () => {
        if (roleToDelete?._id) {
            dispatch(deleteRole(roleToDelete._id));
        }
        setDeleteDialogOpen(false);
        setRoleToDelete(null);
    };

    /** Same flow as pre-restyle RoleRenderer: auth modal when signed out, then setRole + optional callback. */
    const handleApplyClick = (role: IRole) => {
        if (!isSignedIn) {
            dispatch(toggleAuthModal(true));
            return;
        }
        if (isManageMode) {
            onManageClick?.(role);
            return;
        }
        dispatch(setRole(role));
        onApplyClick?.(role);
    };

    const isDark = themeStyle === 'dark';
    const cardBg = isDark ? '#131313' : theme[themeStyle].secondaryBg;

    const SingleRoleRenderer = ({ role, index }: { role: IRole; index: number }) => {
        const typeTag = roleTypeLabel(role);
        const tags = [typeTag, role.employment_type].filter(Boolean) as string[];

        const titleText =
            role.role_type === 'cast' && role.gender
                ? `${role.character} — ${role.gender}, ${role.min_age}–${role.max_age}`
                : role.character;

        return (
            <Box sx={{ width: '100%', mb: 5 }}>
                <Box
                    sx={{
                        width: '100%',
                        bgcolor: cardBg,
                        borderRadius: 3,
                        p: { xs: 2, sm: 3 },
                        border: '1px solid rgba(255,255,255,0.06)',
                        borderLeft: `4px solid ${AUDITION_FIELD_ACCENT}`,
                        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                        '&:hover': {
                            transform: 'scale(1.015)',
                            boxShadow: '0 16px 28px rgba(0,0,0,0.32)',
                        },
                    }}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'stretch', md: 'center' }}
                        justifyContent="space-between">
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: { xs: 20, sm: 24 },
                                    color: theme[themeStyle].tag_white,
                                    fontWeight: 800,
                                    textTransform: 'capitalize',
                                }}>
                                {titleText}
                            </Typography>
                            {tags.length > 0 && (
                                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
                                    {tags.map((tag, i) => {
                                        return (
                                            <Box
                                                key={`${tag}-${i}`}
                                                sx={{
                                                    px: 1.5,
                                                    py: 0.5,
                                                    borderRadius: 1,
                                                    bgcolor: isBrandMode
                                                        ? 'rgba(0,253,193,0.12)'
                                                        : theme[themeStyle].form_gray,
                                                    border: isBrandMode ? 'none' : '1px solid transparent',
                                                    color: isBrandMode
                                                        ? BRAND_LUXE_SECONDARY
                                                        : theme[themeStyle].light_gray_text,
                                                    fontSize: 11,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.12em',
                                                    fontWeight: 600,
                                                }}>
                                                {tag}
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            )}
                        </Box>
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                            sx={{ flexShrink: 0, width: { xs: '100%', md: 'auto' } }}>
                            {!isManageMode && (
                                <CustomButton
                                    label={castingClosed ? 'Closed' : 'Apply'}
                                    disabled={castingClosed}
                                    onClick={() => handleApplyClick(role)}
                                    customStyle={{
                                        borderRadius: 999,
                                        minWidth: 140,
                                        padding: '10px 28px',
                                    }}
                                    textSx={{
                                        color: castingClosed
                                            ? theme[themeStyle].tag_white
                                            : theme[themeStyle].true_white,
                                    }}
                                    sx={{
                                        backgroundColor: castingClosed
                                            ? theme[themeStyle].deep_grey
                                            : AUDITION_FIELD_ACCENT,
                                        border: castingClosed
                                            ? `1px solid ${theme[themeStyle].darkGray60}`
                                            : `1px solid ${AUDITION_FIELD_ACCENT}`,
                                        '&:hover': {
                                            WebkitTextFillColor: theme[themeStyle].true_white,
                                            color: theme[themeStyle].true_white,
                                            backgroundColor: castingClosed
                                                ? theme[themeStyle].deep_grey
                                                : AUDITION_FIELD_ACCENT,
                                            opacity: castingClosed ? 1 : 0.9,
                                            borderColor: castingClosed ? undefined : AUDITION_FIELD_ACCENT,
                                        },
                                    }}
                                />
                            )}
                            {isManageMode && isBrandMode && (
                                <IconButton
                                    onClick={() => handleApplyClick(role)}
                                    sx={{
                                        color: theme[themeStyle].primary_orange,
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 201, 101, 0.14)',
                                        },
                                    }}>
                                    <EditOutlinedIcon />
                                </IconButton>
                            )}
                            {isManageMode && !isBrandMode && (
                                <CustomButton
                                    label="Edit Role"
                                    onClick={() => handleApplyClick(role)}
                                    customStyle={{
                                        borderRadius: isBrandMode ? 6 : 999,
                                        minWidth: 140,
                                        padding: '10px 28px',
                                    }}
                                />
                            )}
                            {isManageMode && (
                                <IconButton
                                    onClick={e => {
                                        e.stopPropagation();
                                        handleDeleteClick(role);
                                    }}
                                    sx={{
                                        color: theme[themeStyle].primary_red,
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 0, 0, 0.1)',
                                        },
                                    }}>
                                    <DeleteIcon />
                                </IconButton>
                            )}
                        </Stack>
                    </Stack>

                    <Box
                        sx={{
                            mt: 3,
                            pt: 3,
                            borderTop: '1px solid rgba(255,255,255,0.06)',
                        }}>
                        <RichTextViewer
                            content={role.description}
                            textStyle={{
                                padding: 0,
                                fontSize: 14,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                color: theme[themeStyle].tag_white,
                                lineHeight: 1.65,
                            }}
                        />
                        {role.role_type === 'cast' && role.min_age != null && role.max_age != null && (
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 13,
                                    color: theme[themeStyle].tag_white,
                                    mt: 2,
                                }}>
                                Ages between {role.min_age} – {role.max_age}
                            </Typography>
                        )}
                        {role.role_type === 'cast' && role.gender && (
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 13,
                                    color: theme[themeStyle].tag_white,
                                    textTransform: 'capitalize',
                                }}>
                                {role.gender}
                            </Typography>
                        )}
                        {role.nudity_or_kissing && (
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 13,
                                    color: theme[themeStyle].primary_red,
                                    fontWeight: 'bold',
                                    mt: 1,
                                }}>
                                Contains nudity or kissing
                            </Typography>
                        )}
                        {role.converted_compensation && (
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 13,
                                    color: theme[themeStyle].tag_white,
                                    fontWeight: 'bold',
                                    mt: 1,
                                }}>
                                Pays {role.converted_compensation.formatted_compensation} (Rates are converted to your
                                local currency based on current exchange rates.)
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>
        );
    };

    if (!roles.length) {
        return null;
    }

    return (
        <Box component="section" sx={{ width: '100%' }}>
            <Stack direction="row" alignItems="center" gap={2} sx={{ mb: 3 }}>
                <AccountCircleOutlined sx={{ color: theme[themeStyle].tag_white, fontSize: 22 }} />
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: { xs: 16, sm: 22 },
                        color: theme[themeStyle].tag_white,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        fontWeight: 800,
                        whiteSpace: 'nowrap',
                    }}>
                    Roles in this call
                </Typography>
                <Box
                    sx={{
                        flex: 1,
                        height: '1px',
                        bgcolor: themeStyle === 'dark' ? 'rgba(255,255,255,0.12)' : theme[themeStyle].darkGray60,
                    }}
                />
            </Stack>

            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                spacing={2}
                sx={{ mb: 2.5 }}>
                <Typography
                    sx={{
                        color: theme[themeStyle].light_gray_text,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: { xs: '0.96rem', sm: '1rem' },
                        lineHeight: 1.55,
                        letterSpacing: '0.01em',
                        maxWidth: 560,
                    }}>
                    Choose the role that best aligns with your skills.
                </Typography>
                {isManageMode && manageControls}
            </Stack>
            {roles.map((role, index) => (
                <SingleRoleRenderer key={role._id} role={role} index={index} />
            ))}
            <DialogueSheet
                open={deleteDialogOpen}
                onClose={() => {
                    setDeleteDialogOpen(false);
                    setRoleToDelete(null);
                }}
                onAgreeClicked={handleConfirmDelete}
                onDisagreeClick={() => {
                    setDeleteDialogOpen(false);
                    setRoleToDelete(null);
                }}
                header="Delete Role"
                body={`Are you sure you want to delete the role "${roleToDelete?.character}"? This action cannot be undone.`}
                positiveText="Delete"
                negativeText="Cancel"
                negtiveTextStyle={{
                    color: 'rgba(237, 224, 212, 0.7)',
                    border: `1px solid ${alpha(BRAND_OUTLINE_VARIANT, 0.3)}`,
                    borderRadius: '8px',
                    padding: '10px 24px',
                    background: 'transparent',
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                }}
                positiveTextStyle={{
                    color: theme[themeStyle].primary_white,
                    border: `1px solid ${theme[themeStyle].primary_red}`,
                    borderRadius: '8px',
                    padding: '10px 28px',
                    background: theme[themeStyle].primary_red,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                }}
                paperStyle={{
                    backgroundColor: alpha(BRAND_SURFACE_CONTAINER_LOW, 0.86),
                    border: `1px solid ${alpha(BRAND_OUTLINE_VARIANT, 0.3)}`,
                    boxShadow: '0 24px 40px rgba(0,0,0,0.45)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: '12px',
                }}
                headerStyle={{
                    color: '#EDE0D4',
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: '2rem',
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em',
                }}
                bodyStyle={{
                    color: '#D6C4AF',
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    fontSize: '1rem',
                    lineHeight: 1.6,
                }}
                negativeButtonSx={{
                    transition: 'transform 0.2s ease, border-color 0.2s ease, color 0.2s ease',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        borderColor: alpha(BRAND_OUTLINE_VARIANT, 0.6),
                        color: '#EDE0D4',
                        backgroundColor: 'transparent',
                    },
                }}
                positiveButtonSx={{
                    transition: 'transform 0.2s ease, filter 0.2s ease, box-shadow 0.2s ease',
                    boxShadow: `0 0 28px ${alpha(theme[themeStyle].primary_red, 0.24)}`,
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        filter: 'brightness(1.02)',
                        boxShadow: `0 10px 34px ${alpha(theme[themeStyle].primary_red, 0.42)}`,
                        backgroundColor: theme[themeStyle].primary_red,
                    },
                }}
            />
        </Box>
    );
};

export default RoleRenderer;
